"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SocketAdapter = void 0;
const domain_1 = require("@home-health-notify/domain");
const socket_io_client_1 = __importDefault(require("socket.io-client"));
const Constants_1 = require("../Constants");
const SocketEvents_1 = require("./SocketEvents");
const DEFAULT_ACK_DETECTION_WINDOW_MS = 30000;
class SocketAdapter {
    constructor(accessTokenProvider, config, authStatusManager) {
        this.messageSendTimestamps = new Map();
        this.messageSendTimeouts = new Map();
        this.messageAckReceived = new Set();
        this.abortController = new AbortController();
        this.handleConnect = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.abortController.signal.aborted)
                return;
            console.log("connect", (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
            this.socketNetworkListener.onConnect();
            const headers = {
                [Constants_1.ApiHeader.DEVICE_TYPE]: this.config.platformType,
                [Constants_1.ApiHeader.APPLICATION_VERSION]: this.config.applicationVersion,
                [Constants_1.ApiHeader.ORGANIZATION_ID]: this.orgData.organizationId,
            };
            const accessToken = yield this.accessTokenProvider.getAccessToken();
            const validToken = yield accessToken.get(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (this.abortController.signal.aborted)
                    return;
                console.log("logoutUser called from SOCKET ADAPTER ==>", accessToken, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
                yield this.authStatusManager.logoutUser();
            }));
            if (validToken && !this.abortController.signal.aborted) {
                console.log("Authenticating socket: emitting AUTHENTICATION event", (_b = this.socket) === null || _b === void 0 ? void 0 : _b.id, this.orgData.organizationId);
                this.socket.emit(SocketEvents_1.SOCKET_EVENT.AUTHENTICATION, {
                    token: validToken,
                    headers,
                });
            }
        });
        this.config = config;
        this.accessTokenProvider = accessTokenProvider;
        this.authStatusManager = authStatusManager;
    }
    acknowledgeAuthentication(message, onSettled) {
        var _a;
        if (this.abortController.signal.aborted)
            return;
        console.log("AUTHENTICATED", message, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
        this.socket.emit(SocketEvents_1.SOCKET_EVENT.CONNECTION_ESTABLISHED, {}, () => {
            var _a;
            console.log("Connection Established", (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
            if (!this.socket) {
                throw new Error(`Panic: Received connection-established event: Org id: ${this.orgData.organizationId}: Expecting socket to be defined!`);
            }
            this.socketNetworkListener.onAuthenticated(this.socket.id);
            onSettled === null || onSettled === void 0 ? void 0 : onSettled();
        });
    }
    // ┌───────────────────┬────────────────────────────────────────────┬──────────────────┐
    // │       Event       │                    Role                    │ Settles promise? │
    // ├───────────────────┼────────────────────────────────────────────┼──────────────────┤
    // │ CONNECT           │ Initiates auth handshake                   │ No               │
    // ├───────────────────┼────────────────────────────────────────────┼──────────────────┤
    // │ CONNECT_ERROR     │ Intermediate failure (retry follows)       │ No — just logs   │
    // ├───────────────────┼────────────────────────────────────────────┼──────────────────┤
    // │ CONNECT_TIMEOUT   │ Intermediate timeout (retry follows)       │ No — just logs   │
    // ├───────────────────┼────────────────────────────────────────────┼──────────────────┤
    // │ AUTHENTICATED     │ Terminal: success                          │ Yes → resolve    │
    // ├───────────────────┼────────────────────────────────────────────┼──────────────────┤
    // │ UNAUTHORIZED      │ Terminal: auth rejected                    │ Yes → reject     │
    // ├───────────────────┼────────────────────────────────────────────┼──────────────────┤
    // │ RECONNECT_FAILED  │ Terminal: all retries exhausted            │ Yes → resolve    │
    // └───────────────────┴────────────────────────────────────────────┴──────────────────┘
    connectAndAuthenticate() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                var _a;
                if (this.socket) {
                    throw new Error(`Already connected to a socket ${(_a = this.socket) === null || _a === void 0 ? void 0 : _a.id}, org id: ${this.orgData.organizationId}`);
                }
                this.socket = (0, socket_io_client_1.default)(this.config.baseUrl, {
                    reconnection: true,
                    autoConnect: true,
                    reconnectionDelay: 100,
                    reconnectionDelayMax: 5000,
                    reconnectionAttempts: 11,
                    randomizationFactor: 0.5,
                    transports: ["websocket"],
                });
                let settled = false;
                const cleanup = () => {
                    var _a;
                    this.socket.off(SocketEvents_1.SOCKET_EVENT.CONNECT, this.handleConnect);
                    this.socket.off(SocketEvents_1.SOCKET_EVENT.AUTHENTICATED, onAuthenticated);
                    this.socket.off(SocketEvents_1.SOCKET_EVENT.UNAUTHORIZED, onUnauthorized);
                    this.socket.off(SocketEvents_1.SOCKET_EVENT.CONNECT_ERROR, onConnectError);
                    this.socket.off(SocketEvents_1.SOCKET_EVENT.CONNECT_TIMEOUT, onConnectTimeout);
                    this.socket.off(SocketEvents_1.SOCKET_EVENT.RECONNECT_FAILED, onReconnectFailed);
                    console.log("cleanup socket", (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
                };
                const settleOnce = (fn) => {
                    if (settled)
                        return;
                    settled = true;
                    cleanup();
                    fn();
                };
                const onAuthenticated = (message) => this.acknowledgeAuthentication(message, () => settleOnce(() => resolve()));
                const onUnauthorized = (data) => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b;
                    console.log("UNAUTHORIZED", data, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
                    console.log("UNAUTHORIZED: rejecting socket", (_b = this.socket) === null || _b === void 0 ? void 0 : _b.id, this.orgData.organizationId);
                    settleOnce(() => reject(new domain_1.ForceLogoutException("Socket: UNAUTHORIZED event")));
                });
                const onConnectError = (data) => {
                    var _a;
                    console.log("CONNECT_ERROR", data, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
                };
                const onConnectTimeout = (data) => {
                    var _a;
                    console.log("CONNECT_TIMEOUT", data, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
                };
                const onReconnectFailed = () => {
                    var _a;
                    console.log("RECONNECT_FAILED", (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
                    this.socketNetworkListener.onReconnectFailed();
                    settleOnce(() => resolve());
                };
                this.socket.on(SocketEvents_1.SOCKET_EVENT.CONNECT, this.handleConnect);
                this.socket.on(SocketEvents_1.SOCKET_EVENT.AUTHENTICATED, onAuthenticated);
                this.socket.on(SocketEvents_1.SOCKET_EVENT.UNAUTHORIZED, onUnauthorized);
                this.socket.on(SocketEvents_1.SOCKET_EVENT.CONNECT_ERROR, onConnectError);
                this.socket.on(SocketEvents_1.SOCKET_EVENT.CONNECT_TIMEOUT, onConnectTimeout);
                this.socket.on(SocketEvents_1.SOCKET_EVENT.RECONNECT_FAILED, onReconnectFailed);
            });
        });
    }
    registerRuntimeNetworkHandlers() {
        var _a;
        // socket.io 2.x deletes socket.id before emitting DISCONNECT, so we
        // capture it here. Seeded with the current id (already authenticated when
        // this method runs) and refreshed on every subsequent reconnect.
        // This is closure-scoped and never leaks to the class.
        const socketIdRef = { current: (_a = this.socket.id) !== null && _a !== void 0 ? _a : "" };
        this.socket.on(SocketEvents_1.SOCKET_EVENT.CONNECT, this.handleConnect);
        this.socket.on(SocketEvents_1.SOCKET_EVENT.AUTHENTICATED, (message) => this.acknowledgeAuthentication(message, () => {
            var _a, _b;
            socketIdRef.current = (_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "";
        }));
        this.socket.on(SocketEvents_1.SOCKET_EVENT.UNAUTHORIZED, (data) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            console.log("UNAUTHORIZED", data, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
            yield this.authStatusManager.logoutUser();
        }));
        this.socket.on(SocketEvents_1.SOCKET_EVENT.DISCONNECT, (data) => {
            var _a;
            // socket.id is already cleared by socket.io 2.x at this point;
            // use the id captured on last successful authentication instead.
            console.log("DISCONNECT", data, socketIdRef.current, this.orgData.organizationId);
            if (!this.socket) {
                throw new Error(`Panic: Received disconnect event: Org id: ${this.orgData.organizationId}: ${data}: Expecting socket to be defined!`);
            }
            this.socketNetworkListener.onDisconnect(socketIdRef.current);
            // https://socket.io/docs/v2/client-api/#event-disconnect
            if (data === "io server disconnect") {
                throw new Error(`Received SOCKET_EVENT.DISCONNECT: Org id: ${this.orgData.organizationId}, Socket id ${(_a = this.socket) === null || _a === void 0 ? void 0 : _a.id}: ${data}`);
                // TODO: Commented to observe if server explictly closes the socket. Remove exeception and uncomment the below code.
                // this.socketNetworkListener.onReconnectFailed();
            }
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.RECONNECT_ATTEMPT, (attemptNumber) => {
            var _a;
            console.log("RECONNECT_ATTEMPT", attemptNumber, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
            this.socketNetworkListener.onReconnectAttempt(attemptNumber);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.RECONNECT_ERROR, (error) => {
            var _a;
            console.log("RECONNECT_ERROR", error, (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.RECONNECT_FAILED, () => {
            var _a;
            console.log("RECONNECT_FAILED", (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, this.orgData.organizationId);
            this.socketNetworkListener.onReconnectFailed();
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.PONG, () => {
            this.socketNetworkListener.onPong();
        });
    }
    registerRuntimeDomainHandlers() {
        this.socket.on(SocketEvents_1.SOCKET_EVENT.CHAT_MESSAGE, (data) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            console.warn(`[DEBUG socket] CHAT_MESSAGE | chatId: ${(_a = data.chatLog) === null || _a === void 0 ? void 0 : _a.from} | messageId: ${(_b = data.message) === null || _b === void 0 ? void 0 : _b.messageId} | unreadCount: ${(_c = data.chatLog) === null || _c === void 0 ? void 0 : _c.unreadCount} | sender: ${(_d = data.message) === null || _d === void 0 ? void 0 : _d.from}`);
            const messageId = (_e = data.message) === null || _e === void 0 ? void 0 : _e.messageId;
            const sendStartTime = messageId && this.messageSendTimestamps.get(messageId);
            if (sendStartTime) {
                const durationMs = Date.now() - sendStartTime;
                this.messageSendTimestamps.delete(messageId);
                clearTimeout(this.messageSendTimeouts.get(messageId));
                this.messageSendTimeouts.delete(messageId);
                console.log("Message full round-trip duration:", durationMs, "ms, threshold:", this.config.slowMessageThresholdMs, "ms, messageId:", (_f = data.message) === null || _f === void 0 ? void 0 : _f.messageId, "chatId:", (_g = data.message) === null || _g === void 0 ? void 0 : _g.to, "ms, socketId:", (_h = this.socket) === null || _h === void 0 ? void 0 : _h.id);
                if (durationMs > this.config.slowMessageThresholdMs) {
                    this.domainListener.onSlowMessageSend({
                        messageId,
                        durationMs,
                        chatId: (_j = data.chatLog) === null || _j === void 0 ? void 0 : _j.from,
                        socketConnected: (_k = this.socket) === null || _k === void 0 ? void 0 : _k.connected,
                        socketId: (_l = this.socket) === null || _l === void 0 ? void 0 : _l.id,
                    });
                }
            }
            this.domainListener.onReceiveMessage(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.V2_NEW_ALERT, (data) => {
            console.warn(`[DEBUG socket] V2_NEW_ALERT | alertId: ${data.alertId || data.id}`);
            this.domainListener.onReceiveAlert(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.NEW_ALERT, (data) => {
            console.warn(`[DEBUG socket] NEW_ALERT | alertId: ${data.alertId || data.id}`);
            this.domainListener.onReceiveReferralAlert(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.V2_REMOVE_ALERT, (data) => {
            console.warn(`[DEBUG socket] V2_REMOVE_ALERT | alertId: ${data.alertId || data.id}`);
            this.domainListener.onV2AlertRemove(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.REMOVE_ALERT, (data) => {
            console.warn(`[DEBUG socket] REMOVE_ALERT | alertId: ${data.alertId || data.id}`);
            this.domainListener.onAlertRemove(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.MESSAGE_UPDATE, (data) => {
            var _a, _b;
            console.warn(`[DEBUG socket] MESSAGE_UPDATE | messageId: ${data.messageId || ((_a = data.message) === null || _a === void 0 ? void 0 : _a.messageId)} | chatId: ${data.to || ((_b = data.message) === null || _b === void 0 ? void 0 : _b.to)}`);
            this.domainListener.onMessageUpdated(this.orgData.organizationId, data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.MESSAGE_DELETE, (data) => {
            var _a, _b;
            console.warn(`[DEBUG socket] MESSAGE_DELETE | messageId: ${data.messageId || ((_a = data.message) === null || _a === void 0 ? void 0 : _a.messageId)} | chatId: ${data.to || ((_b = data.message) === null || _b === void 0 ? void 0 : _b.to)}`);
            this.domainListener.onMessageDeleted(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.NO_PENDING_ACKNOWLEDGE, (data) => {
            console.warn(`[DEBUG socket] NO_PENDING_ACKNOWLEDGE | chatId: ${data.from || data.chatId}`);
            this.domainListener.onNoPendingAcknowledgement(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.GROUP_UPDATE, (data) => {
            console.warn(`[DEBUG socket] GROUP_UPDATE | chatId: ${data.from || data.chatId || data.groupId}`);
            this.domainListener.onUpdateGroup(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.CHAT_LOG_UPDATE, (data) => {
            console.warn(`[DEBUG socket] CHAT_LOG_UPDATE | chatId: ${data.from || data.chatId} | unreadCount: ${data.unreadCount} | lastMessageRead: ${data.lastMessageRead}`);
            this.domainListener.onChatLogUpdate(data, this.orgData.organizationId);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.NEW_CHAT, (data) => {
            console.warn(`[DEBUG socket] NEW_CHAT | chatId: ${data.from || data.chatId} | chatType: ${data.chatType} | groupType: ${data.groupType}`);
            this.domainListener.onNewChat(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.HIDE_CHAT, (data) => {
            console.warn(`[DEBUG socket] HIDE_CHAT | data: ${JSON.stringify(data)}`);
            this.domainListener.onHideChat(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.UNHIDE_CHAT, (data) => {
            console.warn(`[DEBUG socket] UNHIDE_CHAT | data: ${JSON.stringify(data)}`);
            this.domainListener.onUnHideChat(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.CHAT_DELETED, (data) => {
            console.warn(`[DEBUG socket] CHAT_DELETED | chatId: ${data.from || data.chatId}`);
            this.domainListener.onChatDelete(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.MESSAGE_DELIVERED, (data) => {
            var _a, _b;
            console.warn(`[DEBUG socket] MESSAGE_DELIVERED | messageId: ${data.messageId || ((_a = data.message) === null || _a === void 0 ? void 0 : _a.messageId)} | chatId: ${data.to || ((_b = data.message) === null || _b === void 0 ? void 0 : _b.to)}`);
            this.domainListener.onMessageDelivered(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.MESSAGE_READ, (data) => {
            var _a, _b;
            console.warn(`[DEBUG socket] MESSAGE_READ | messageId: ${data.messageId || ((_a = data.message) === null || _a === void 0 ? void 0 : _a.messageId)} | chatId: ${data.to || ((_b = data.message) === null || _b === void 0 ? void 0 : _b.to)}`);
            this.domainListener.onMessageRead(data);
        });
        this.socket.on(SocketEvents_1.SOCKET_EVENT.USER_UPDATE, (data) => {
            console.warn(`[DEBUG socket] USER_UPDATE | userId: ${data.userId || data.id}`);
            this.domainListener.onUserUpdate(data);
        });
    }
    open(orgData, domainListener, socketNetworkListner) {
        return __awaiter(this, void 0, void 0, function* () {
            this.abortController = new AbortController();
            this.orgData = orgData;
            this.domainListener = domainListener;
            this.socketNetworkListener = socketNetworkListner;
            yield this.connectAndAuthenticate();
            this.registerRuntimeDomainHandlers();
            this.registerRuntimeNetworkHandlers();
        });
    }
    actionOnReminder(data, responseCallback) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.ACTION_ON_REMINDER,
            data,
        };
        this.executeEvent(socketData, responseCallback);
    }
    sendMessage(message, responseCallback, onTimeout) {
        this.messageSendTimestamps.set(message.messageId, Date.now());
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.SEND_MESSAGE,
            data: message,
        };
        // Exactly one of the ack and the timeout reaches the caller: a late ack
        // would otherwise contradict what the user has already been told. The
        // bookkeeping below is deliberately outside that gate, so a late ack is
        // still recorded for the slow-send report.
        let settled = false;
        const ackTimer = onTimeout
            ? setTimeout(() => {
                if (settled) {
                    return;
                }
                settled = true;
                onTimeout();
            }, this.ackDetectionWindowMs())
            : undefined;
        this.executeEvent(socketData, (data, payload) => {
            var _a;
            this.messageAckReceived.add(message.messageId);
            // No CHAT_MESSAGE follows a duplicate, and that echo is what normally
            // clears this bookkeeping. Left armed, the timer below reports a send
            // we just recovered as a timeout.
            if (data.statusCode !== 200 || ((_a = data.data) === null || _a === void 0 ? void 0 : _a.duplicate)) {
                this.messageSendTimestamps.delete(message.messageId);
                this.messageAckReceived.delete(message.messageId);
                clearTimeout(this.messageSendTimeouts.get(message.messageId));
                this.messageSendTimeouts.delete(message.messageId);
            }
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(ackTimer);
            responseCallback === null || responseCallback === void 0 ? void 0 : responseCallback(data, payload);
        });
        const timeoutId = setTimeout(() => {
            var _a, _b, _c;
            const sendStartTime = this.messageSendTimestamps.get(message.messageId);
            if (sendStartTime) {
                const durationMs = Date.now() - sendStartTime;
                const ackReceived = this.messageAckReceived.has(message.messageId);
                this.messageSendTimestamps.delete(message.messageId);
                this.messageAckReceived.delete(message.messageId);
                console.log("Message response timed out:", durationMs, "ms, messageId:", message.messageId, "chatId:", message.to, "socketId:", (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id, "ackReceived:", ackReceived);
                this.domainListener.onSlowMessageSend({
                    messageId: message.messageId,
                    durationMs,
                    chatId: message.to,
                    socketConnected: (_b = this.socket) === null || _b === void 0 ? void 0 : _b.connected,
                    socketId: (_c = this.socket) === null || _c === void 0 ? void 0 : _c.id,
                    timedOut: true,
                    ackReceived,
                });
            }
            this.messageSendTimeouts.delete(message.messageId);
        }, this.config.slowMessageThresholdMs);
        this.messageSendTimeouts.set(message.messageId, timeoutId);
    }
    editMessage(message, responseCallback, onTimeout) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.EDIT_MESSAGE,
            data: message,
        };
        let settled = false;
        const ackTimer = onTimeout
            ? setTimeout(() => {
                var _a, _b;
                if (settled) {
                    return;
                }
                settled = true;
                console.warn("Edit message acknowledgement timed out:", {
                    messageId: message._id,
                    socketId: (_a = this.socket) === null || _a === void 0 ? void 0 : _a.id,
                    socketConnected: (_b = this.socket) === null || _b === void 0 ? void 0 : _b.connected,
                });
                onTimeout();
            }, this.ackDetectionWindowMs())
            : undefined;
        this.executeEvent(socketData, (data, payload) => {
            if (settled) {
                return;
            }
            settled = true;
            if (ackTimer) {
                clearTimeout(ackTimer);
            }
            responseCallback === null || responseCallback === void 0 ? void 0 : responseCallback(data, payload);
        });
    }
    deleteMessage(message) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.DELETE_MESSAGE,
            data: {
                payload: [message],
            },
        };
        this.executeEvent(socketData);
    }
    markMessageAsRead(message, responseCallback) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.MARK_AS_READ,
            data: message,
        };
        this.executeEvent(socketData, responseCallback);
    }
    markMessageAsDelivered(message) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.MARK_AS_DELIVERED,
            data: message,
        };
        this.executeEvent(socketData);
    }
    sendAcknowledgment(message) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.ACKNOWLEDGE_MESSAGE,
            data: message,
        };
        this.executeEvent(socketData);
    }
    askAcknowledgment(message) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.ASK_FOR_ACKNOWLEDGEMENT,
            data: message,
        };
        this.executeEvent(socketData);
    }
    forwardMessage(payload, responseCallback, onTimeout) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.FORWARD_MESSAGE,
            data: payload,
        };
        if (!onTimeout) {
            this.executeEvent(socketData, responseCallback);
            return;
        }
        // Exactly one of the two fires: a late ack after the timeout would
        // otherwise contradict the message the user has already been shown.
        let settled = false;
        const timer = setTimeout(() => {
            if (settled) {
                return;
            }
            settled = true;
            onTimeout();
        }, this.ackDetectionWindowMs());
        this.executeEvent(socketData, (data, payload) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            responseCallback === null || responseCallback === void 0 ? void 0 : responseCallback(data, payload);
        });
    }
    /**
     * How long to wait before concluding an acknowledgement is not coming.
     * socket.io only notices a dead connection after a missed ping plus its
     * timeout, and until then it still reports itself connected — so waiting any
     * less says "no answer" while the transport still believes it is fine.
     *
     * The values come from the server on handshake; the fallback matches the
     * protocol defaults for when the engine has not reported them yet.
     */
    ackDetectionWindowMs() {
        var _a, _b;
        const engine = (_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.io) === null || _b === void 0 ? void 0 : _b.engine;
        const pingInterval = Number(engine === null || engine === void 0 ? void 0 : engine.pingInterval);
        const pingTimeout = Number(engine === null || engine === void 0 ? void 0 : engine.pingTimeout);
        return Number.isFinite(pingInterval) && Number.isFinite(pingTimeout)
            ? pingInterval + pingTimeout
            : DEFAULT_ACK_DETECTION_WINDOW_MS;
    }
    markPatientAsVisible(data, responseCallback) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.VISIBLE_TO_PATIENT_PHYSICIAN,
            data,
        };
        this.executeEvent(socketData, responseCallback);
    }
    getMediaSignedUrl(url, responseCallback) {
        const socketData = {
            event: SocketEvents_1.SOCKET_EVENT.GET_MEDIA_SIGNED_URL,
            data: {
                url,
            },
        };
        this.executeEvent(socketData, responseCallback);
    }
    executeEvent(socketData, responseCallback) {
        this.emit("submit", socketData, (data) => {
            responseCallback && responseCallback(data, socketData);
            if (data.statusCode && data.statusCode !== 200) {
                this.domainListener.onSocketResponseError(data);
            }
        });
    }
    emit(event, payload, cb) {
        if (this.abortController.signal.aborted)
            return;
        payload.headers = Object.assign(Object.assign({}, payload.headers), { [Constants_1.ApiHeader.DEVICE_TYPE]: this.config.platformType, [Constants_1.ApiHeader.APPLICATION_VERSION]: this.config.applicationVersion, [Constants_1.ApiHeader.API_VERSION]: this.config.apiVersion, [Constants_1.ApiHeader.ORGANIZATION_ID]: this.orgData.organizationId });
        this.socket.emit(event, payload, cb);
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            this.abortController.abort();
            return new Promise((resolve, reject) => {
                if (this.socket) {
                    this.socket.removeAllListeners();
                    if (this.socket.connected) {
                        this.socket.on(SocketEvents_1.SOCKET_EVENT.DISCONNECT, () => {
                            try {
                                this.socket = null;
                                resolve();
                            }
                            catch (error) {
                                reject();
                            }
                        });
                        this.socket.close();
                    }
                    else {
                        this.socket = null;
                        resolve();
                    }
                }
                else {
                    resolve();
                }
            });
        });
    }
    isConnected() {
        var _a, _b;
        return (_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.connected) !== null && _b !== void 0 ? _b : false;
    }
}
exports.SocketAdapter = SocketAdapter;
